<template>
    <v-app class="overflow-hidden" id="layout">
        <v-defaults-provider :defaults="defaultProvider">
            <v-app-bar
                aspect-ratio="1"
                flat
                absolute
                :height="80"
                style="z-index: 1005"
                class="drop-shadow-xl admin-layout md:pl-9 md:pr-10"
            >
                <navigation-bar
                    :admin="true"
                    :countMenu="countMenu"
                    :menu="menu"
                    @emitDrawer="handleDrawer"
                    :drawer="drawer"
                ></navigation-bar>
            </v-app-bar>

            <!-- start v-navigation-drawer -->
            <v-navigation-drawer
                :style="
                    $vuetify.display.smAndDown
                        ? 'height: fit-content !important'
                        : ''
                "
                :location="$vuetify.display.smAndDown ? 'top' : 'left'"
                v-model="drawer"
                color="transparent"
                class="border-none !shadow-none"
                :class="{
                    '!w-[100%]': $vuetify.display.smAndDown,
                    '!w-[270px]': $vuetify.display.mdAndUp,
                }"
            >
                <v-list
                    rounded="lg"
                    density="default"
                    nav
                    bg-color="white"
                    class="md: mx-5 my-5 !rounded-2xl"
                    :class="{
                        '!h-fit': $vuetify.display.smAndDown,
                        'h-[calc(100%-50px)]': $vuetify.display.mdAndUp,
                    }"
                >
                    <template v-for="m in menu">
                        <v-list-item
                            rounded="lg"
                            :key="m.title"
                            v-if="m.type == 'btn' && (Array.isArray(m.access) ? m.access.some(a => $can('read', a)) : $can('read', m.access))"
                            :title="m.title"
                            :to="{ name: m.name }"
                        >
                            <template #prepend>
                                <v-icon :class="m.icon"></v-icon>
                            </template>
                        </v-list-item>
                        <v-list-group
                            :key="m.title"
                            v-if="
                                m.type == 'dropdown' && (Array.isArray(m.access) ? m.access.some(a => $can('read', a)) : $can('read', m.access))
                            "
                            :value="m.title"
                        >
                            <template v-slot:activator="{ props }">
                                <v-list-item v-bind="props" title="Master Data">
                                    <template #prepend>
                                        <v-icon :class="m.icon"></v-icon>
                                    </template>
                                </v-list-item>
                            </template>

                            <template v-for="l in m.list">
                                <v-list-item
                                    :key="l.title"
                                    v-if="(Array.isArray(l.access) ? l.access.some(a => $can('read', a)) : $can('read', l.access))"
                                    :title="l.title"
                                    :to="{ name: l.name }"
                                    link
                                >
                                    <template #prepend>
                                        <v-icon :class="l.icon"></v-icon>
                                    </template>
                                </v-list-item>
                            </template>
                        </v-list-group>
                    </template>
                </v-list>
            </v-navigation-drawer>
            <!-- end v-navigation-drawer -->

            <!-- app bar top -->

            <v-main id="main" class="layout-page-content-admin">
                <!-- <v-container> -->
                <router-view v-slot="{ Component }">
                    <v-slide-y-transition mode="out-in">
                        <component :is="Component" />
                    </v-slide-y-transition>
                </router-view>
                <div
                    :style="
                        $vuetify.display.mdAndUp
                            ? 'height: 160px'
                            : 'height: 80px'
                    "
                ></div>
                <div class="footer mt-10" v-if="$vuetify.display.mdAndUp">
                    <v-divider class="w-full pl-1"></v-divider>
                    <div
                        class="content flex justify-between align-center !text-gray-500 text-sm"
                    >
                        <div>
                            Copyright © 2024
                            <br />
                            IT System Development - RSUD Cibinong
                        </div>
                        <!-- <div class="text-right">
                            <a href="www.freepik.com" target="_blank">Freepik</a>
                        </div> -->
                    </div>
                </div>
                <!-- </v-container> -->
            </v-main>
        </v-defaults-provider>
    </v-app>
</template>

<script setup>
import { asset, replaceAvaByDefault } from "@/mixins";
import menu from "@/menuAdmin";
import NavigationBar from "./NavigationBar.vue";
import BottomAppBar from "./component/BottomAppBar.vue";
import {
    ref,
    onMounted,
    computed,
    provide,
    reactive,
    onBeforeUnmount,
} from "vue";
import { useTheme, useDisplay } from "vuetify";
import ability from "@/plugins/casl/ability";

import { useAuthStore } from "@/store/auth.js";
const authStore = useAuthStore();

const theme = useTheme();
const { xs, lgAndUp } = useDisplay();
const isDark = computed(() => {
    let current = theme.current.value;
    return current.dark;
});

const countMenu = computed(() => {
    return menu.filter((f) => {
        if (Array.isArray(f.access)) {
            return f.access.some((a) => ability.can("read", a));
        }
        return ability.can("read", f.access);
    }).length;
});
const isDisplay = computed(() => {
    return xs;
});

const clickOutsideDrawer = (e) => {
    nextTick(() => {
        if (drawer.value) {
            drawer.value = false;
        }
    });
};

provide("$isDark", isDark);

const defaultProvider = reactive({
    VCard: {
        border: isDark,
    },
    VToolbar: {
        border: isDark,
    },
    VDialogFull: {
        fullscreen: xs,
        persistent: true,
        scrollable: true,
    },
});

const drawer = ref(lgAndUp);
const offsetTop = ref(0);

const handleDrawer = () => {
    drawer.value = !drawer.value;
};

const onScroll = () => {
    offsetTop.value = window.top.scrollY;
};

const offline = ref(false);
function handleConnectionChange() {
    offline.value = !navigator.onLine;
}

const banner = ref(false);
provide("$banner", banner);
let deferredPrompt = ref(window._deferredPrompt);
provide("$deferredPrompt", deferredPrompt);

async function installApp() {
    banner.value = false;
    if (deferredPrompt.value) {
        deferredPrompt.value.prompt();
        const { outcome } = await deferredPrompt.value.userChoice;
        if (outcome === "accepted") {
            deferredPrompt.value = null;
        } else {
            deferredPrompt.value = window._deferredPrompt;
        }
    }
}

function installLater() {
    localStorage.setItem("installLater", JSON.stringify(true));
    banner.value = false;
}

onMounted(() => {
    window.addEventListener("scroll", onScroll);
    window.addEventListener("offline", handleConnectionChange);
    window.addEventListener("online", handleConnectionChange);
    let install = JSON.parse(localStorage.getItem("installLater"));
    if (
        !install &&
        !authStore.isIos &&
        !window.matchMedia("(display-mode: standalone)").matches
    ) {
        setTimeout(() => {
            banner.value = true;
        }, 10000);
    }
});

onBeforeUnmount(() => {
    window.removeEventListener("scroll", onScroll);
    window.removeEventListener("offline", handleConnectionChange);
    window.removeEventListener("online", handleConnectionChange);
});

// app.config.globalProperties.$offsetTop = offsetTop.value;
</script>

<style scoped>
@media only screen and (min-width: 600px) {
    .layout-page-content-admin {
        display: flex;
        align-items: flex-start;
        flex-direction: column;
        flex-grow: 1;
        margin-top: 2rem;
        margin-left: 1rem;
        margin-right: 1rem;
    }
}

.layout-page-content-admin {
    display: flex;
    align-items: flex-start;
    flex-direction: column;
    flex-grow: 1;
}

/* Remove the previous :deep(.v-navigation-drawer) styles */

:deep(.v-navigation-drawer) {
    position: absolute !important;
    height: calc(100% - 80px) !important;
}

:deep(.drawer-list) {
    height: 100%;
    overflow-y: auto;
}

.footer {
    position: absolute;
    width: 100%;
    bottom: 10px;
}

.footer .content {
    height: 70px;
    padding-left: 7%;
    padding-right: 7%;
}

.accelerate {
    transform: translateZ(0) !important;
    transform: translate3d(0, 0, 0) !important;
    will-change: transform, opacity;
}
</style>
